import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { useAgentStreamData } from '@reflex/ui/components/agent/AgentStreamDataContext';
import { cn } from '@reflex/ui/lib/utils';

/**
 * Popup card surfaced when the replay engine reaches a timeline
 * annotation gate. Reads {@link AgentStreamData.pendingReplayAnnotation}
 * from context and renders an explanatory card anchored to the chat
 * bubble the annotation is attached to (via `data-event-id`). Clicking
 * the "Okay" button calls
 * {@link AgentStreamData.dismissReplayAnnotation}; in non-interactive
 * playback a thin progress fill on the button drains over
 * `autoDismissMs` so the viewer can see the countdown but can still
 * click to dismiss early.
 *
 * The overlay renders **inline** inside its replay container — it
 * does not portal to `document.body`. The closest positioned ancestor
 * (the `relative` wrapper inside `EmbeddableReplay`) becomes the
 * offset parent, so the spotlight, popup, and tail are all clipped
 * to the replay's bounding box. Marketing pages that embed a small
 * replay no longer get the whole viewport dimmed.
 *
 * Returns `null` in live sessions and any time the provider isn't
 * surfacing a pending annotation, so it can be mounted unconditionally
 * inside the replay viewer.
 */
export function ReplayAnnotationOverlay({ className }: { className?: string }) {
  const { pendingReplayAnnotation, dismissReplayAnnotation } = useAgentStreamData();

  if (!pendingReplayAnnotation) return null;

  return (
    <ReplayAnnotationCard
      key={pendingReplayAnnotation.id}
      annotation={pendingReplayAnnotation}
      onDismiss={dismissReplayAnnotation}
      className={className}
    />
  );
}

interface ReplayAnnotationCardProps {
  annotation: NonNullable<ReturnType<typeof useAgentStreamData>['pendingReplayAnnotation']>;
  onDismiss?: () => void;
  className?: string;
}

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const POPUP_GAP = 12;
const CONTAINER_MARGIN = 8;
const SPOTLIGHT_PAD = 8;
const TAIL_MIN_INSET = 16;

/**
 * Track the bounding rect of the first chat bubble whose
 * `data-event-id` matches one of `candidateEventIds`, expressed in
 * coordinates **relative to `containerEl`** so the overlay can stay
 * scoped to the replay container instead of the viewport.
 *
 * We accept a list rather than a single id because not every
 * AxonEvent renders as a chat bubble (lifecycle, setup, grouped
 * tool calls) — the overlay needs to fall back to an earlier event
 * that *does* have a bubble. Candidates are scanned in order, so
 * the provider passes them newest-first.
 *
 * The rAF loop is deliberate — chat content frequently animates in
 * and the rect can shift between paints.
 */
function useAnchorRect(
  containerEl: HTMLElement | null,
  candidateEventIds: readonly string[] | undefined,
): AnchorRect | null {
  const [rect, setRect] = useState<AnchorRect | null>(null);
  // Stable key so the effect doesn't re-run when an upstream callsite
  // hands us a freshly-allocated but equal array each render.
  const key = (candidateEventIds ?? []).join('|');

  useEffect(() => {
    const ids = key ? key.split('|') : [];
    if (ids.length === 0 || !containerEl) {
      setRect(null);
      return;
    }
    let raf = 0;
    let stopped = false;

    function findElement(): HTMLElement | null {
      for (const id of ids) {
        const el = (containerEl as HTMLElement).querySelector<HTMLElement>(
          `[data-event-id="${CSS.escape(id)}"]`,
        );
        if (el) return el;
      }
      return null;
    }

    function measure() {
      if (stopped) return;
      const el = findElement();
      if (el && containerEl) {
        const elBox = el.getBoundingClientRect();
        const containerBox = containerEl.getBoundingClientRect();
        const next: AnchorRect = {
          top: elBox.top - containerBox.top,
          left: elBox.left - containerBox.left,
          width: elBox.width,
          height: elBox.height,
        };
        setRect((prev) => {
          if (
            prev &&
            prev.top === next.top &&
            prev.left === next.left &&
            prev.width === next.width &&
            prev.height === next.height
          ) {
            return prev;
          }
          return next;
        });
      } else {
        setRect((prev) => (prev === null ? prev : null));
      }
      raf = requestAnimationFrame(measure);
    }
    measure();

    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [key, containerEl]);

  return rect;
}

/**
 * Track the size of the replay container so placement math can clamp
 * the popup to the container's bounds (instead of the viewport).
 * Updates on resize via {@link ResizeObserver}.
 */
function useContainerSize(el: HTMLElement | null): { width: number; height: number } {
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  useLayoutEffect(() => {
    if (!el) return;
    const update = () => {
      const next = { width: el.clientWidth, height: el.clientHeight };
      setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return size;
}

/**
 * Position the popup relative to the anchor bubble in
 * **container-local** coordinates. Prefers placement directly below
 * the bubble; flips above when there isn't enough room. Horizontally
 * centers on the bubble and clamps to the container edges so the card
 * never spills past the replay's bounding box. Falls back to a fixed
 * bottom-center placement (still container-scoped) when no anchor is
 * available.
 */
function computePlacement(
  rect: AnchorRect | null,
  popupSize: { width: number; height: number },
  containerSize: { width: number; height: number },
): { style: React.CSSProperties; side: 'above' | 'below' | 'center' } {
  if (!rect || popupSize.width === 0 || popupSize.height === 0) {
    return {
      style: {
        position: 'absolute',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
      },
      side: 'center',
    };
  }
  const containerH = containerSize.height || 0;
  const containerW = containerSize.width || 0;

  const fitsBelow =
    rect.top + rect.height + POPUP_GAP + popupSize.height <= containerH - CONTAINER_MARGIN;
  const side: 'above' | 'below' = fitsBelow ? 'below' : 'above';

  const top =
    side === 'below'
      ? rect.top + rect.height + POPUP_GAP
      : Math.max(CONTAINER_MARGIN, rect.top - POPUP_GAP - popupSize.height);

  const desiredLeft = rect.left + rect.width / 2 - popupSize.width / 2;
  const left = Math.max(
    CONTAINER_MARGIN,
    Math.min(
      desiredLeft,
      Math.max(CONTAINER_MARGIN, containerW - popupSize.width - CONTAINER_MARGIN),
    ),
  );

  return {
    style: { position: 'absolute', top, left, width: popupSize.width || undefined },
    side,
  };
}

/**
 * Visual card. Extracted as a separate component so the auto-dismiss
 * progress animation can be keyed to the annotation id and restart
 * cleanly each time the engine surfaces a new gate.
 */
function ReplayAnnotationCard({ annotation, onDismiss, className }: ReplayAnnotationCardProps) {
  const { id, title, body, interactive, autoDismissMs, candidateEventIds } = annotation;
  const [progress, setProgress] = useState(1);
  const startedAtRef = useRef<number>(performance.now());
  const cardRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
  const [popupSize, setPopupSize] = useState({ width: 0, height: 0 });

  // The overlay's offset parent is the closest positioned ancestor of
  // its root sentinel (typically the `relative` wrapper inside
  // `EmbeddableReplay`). Captured in state so children can reactively
  // remeasure when the container changes (e.g. story decorators).
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const parent = root.offsetParent as HTMLElement | null;
    setContainerEl(parent ?? root.parentElement);
  }, []);

  const containerSize = useContainerSize(containerEl);
  const anchor = useAnchorRect(containerEl, candidateEventIds);

  // Drain the progress ring on the dismiss button in non-interactive
  // playback. Interactive playback skips the timer entirely so the
  // viewer can take their time reading the popup.
  useEffect(() => {
    if (interactive || !autoDismissMs) {
      setProgress(1);
      return;
    }
    startedAtRef.current = performance.now();
    let raf = 0;
    function tick() {
      const elapsed = performance.now() - startedAtRef.current;
      const remaining = Math.max(0, 1 - elapsed / autoDismissMs!);
      setProgress(remaining);
      if (remaining > 0) {
        raf = requestAnimationFrame(tick);
      }
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [interactive, autoDismissMs, id]);

  // Measure the card's natural size after each render so
  // `computePlacement` can flip / clamp correctly. ResizeObserver keeps
  // it accurate when the body wraps onto extra lines or the container
  // shrinks.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const update = () => {
      const next = { width: el.offsetWidth, height: el.offsetHeight };
      setPopupSize((prev) =>
        prev.width === next.width && prev.height === next.height ? prev : next,
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { style: positionStyle, side } = computePlacement(anchor, popupSize, containerSize);

  // Tail (arrow) horizontal position in popup-local coordinates. We
  // align with the bubble's center so the arrow keeps pointing at the
  // bubble even when the popup itself was clamped to the container
  // edge. Clamping to TAIL_MIN_INSET keeps the tail clear of the
  // popup's rounded corners.
  const popupLeftPx = typeof positionStyle.left === 'number' ? positionStyle.left : 0;
  const tailLeft =
    anchor && side !== 'center' && popupSize.width > 0
      ? Math.max(
          TAIL_MIN_INSET,
          Math.min(popupSize.width - TAIL_MIN_INSET, anchor.left + anchor.width / 2 - popupLeftPx),
        )
      : popupSize.width / 2;

  return (
    <div
      ref={rootRef}
      data-testid="replay-annotation-root"
      className="pointer-events-none absolute inset-0 z-40"
    >
      <Spotlight rect={anchor} />
      <div
        ref={cardRef}
        data-testid="replay-annotation"
        data-annotation-id={id}
        data-annotation-mode={interactive ? 'interactive' : 'auto'}
        data-annotation-side={side}
        style={positionStyle}
        className={cn(
          'pointer-events-auto z-50 flex w-[min(22rem,calc(100%-1.5rem))] flex-col rounded-xl border border-border/60 bg-popover/95 text-popover-foreground shadow-2xl ring-1 ring-primary/10 backdrop-blur-md',
          className,
        )}
        role="dialog"
        aria-live="polite"
        aria-label={title ?? 'Replay annotation'}
      >
        {side !== 'center' && (
          <div
            data-testid="replay-annotation-tail"
            aria-hidden
            className={cn(
              'pointer-events-none absolute h-3 w-3 bg-popover',
              side === 'below'
                ? 'top-[-7px] border-l border-t border-border/60'
                : 'bottom-[-7px] border-r border-b border-border/60',
            )}
            style={{ left: tailLeft, transform: 'translateX(-50%) rotate(45deg)' }}
          />
        )}
        <div className="flex items-start gap-3 p-4 pb-3">
          <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary ring-1 ring-inset ring-primary/20">
            <Sparkles className="h-3.5 w-3.5" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {title && (
              <p className="text-sm font-semibold leading-tight tracking-tight text-popover-foreground">
                {title}
              </p>
            )}
            <AnnotationBodyMarkdown body={body} />
          </div>
        </div>
        <div className="flex items-center justify-end rounded-b-xl border-t border-border/40 bg-muted/30 px-3 py-2">
          <DismissButton
            onClick={onDismiss}
            showCountdown={!interactive && Boolean(autoDismissMs)}
            progress={progress}
          />
        </div>
      </div>
    </div>
  );
}

interface SpotlightProps {
  rect: AnchorRect | null;
}

/**
 * Dim the replay container and cut out a glowing primary ring around
 * the anchor bubble. When no anchor is available (annotation shown
 * before the very first event), falls back to a plain full-container
 * dim so the popup still reads as a tutorial overlay.
 *
 * Both variants are scoped to the replay container via
 * `position: absolute; inset: 0` on the parent overlay root, so
 * nothing outside the replay's bounding box gets dimmed. The cutout
 * variant uses an outsized `box-shadow` (clipped by the container's
 * `overflow-hidden`) so we can keep the single-element ring trick
 * without an SVG mask.
 */
function Spotlight({ rect }: SpotlightProps) {
  if (!rect) {
    return (
      <div
        data-testid="replay-annotation-spotlight"
        data-spotlight-mode="full"
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-black/40"
        style={{ zIndex: 41 }}
      />
    );
  }
  return (
    <div
      data-testid="replay-annotation-spotlight"
      data-spotlight-mode="cutout"
      aria-hidden
      style={{
        position: 'absolute',
        top: rect.top - SPOTLIGHT_PAD,
        left: rect.left - SPOTLIGHT_PAD,
        width: rect.width + SPOTLIGHT_PAD * 2,
        height: rect.height + SPOTLIGHT_PAD * 2,
        borderRadius: 12,
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)',
        pointerEvents: 'none',
        zIndex: 41,
      }}
      className="ring-2 ring-primary/80 animate-pulse"
    />
  );
}

interface DismissButtonProps {
  onClick?: () => void;
  /** When true, draw a thin draining bar inside the button. */
  showCountdown: boolean;
  /** Remaining auto-dismiss progress, 1 → 0. Ignored when `!showCountdown`. */
  progress: number;
}

/**
 * "Okay" button used by both interactive and auto-dismiss modes. The
 * countdown variant overlays a thin progress bar inside the button so
 * the timer is visible without replacing the click affordance — this
 * guarantees the viewer always has something to click instead of
 * staring at a bare progress strip.
 */
function DismissButton({ onClick, showCountdown, progress }: DismissButtonProps) {
  return (
    <Button
      type="button"
      size="sm"
      onClick={onClick}
      data-testid="replay-annotation-okay"
      className="relative overflow-hidden"
    >
      <span className="relative z-10">Okay</span>
      {showCountdown && (
        <span
          aria-hidden
          data-testid="replay-annotation-progress"
          className="absolute inset-x-0 bottom-0 h-0.5 bg-primary-foreground/70 transition-[width] duration-100 ease-linear"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      )}
    </Button>
  );
}

/**
 * Renders the annotation body as inline markdown, supporting bold,
 * italic, inline code, and links. Scoped to inline elements only —
 * headings, lists, and HTML blocks are stripped. Paragraph breaks
 * from `\n\n` are preserved via the `p` mapping.
 */
const AnnotationBodyMarkdown = memo(function AnnotationBodyMarkdown({ body }: { body: string }) {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      allowedElements={['p', 'strong', 'em', 'code', 'a']}
      unwrapDisallowed
      components={{
        p: ({ children }) => (
          <p
            className="text-[13px] leading-relaxed text-popover-foreground/75 [&:not(:last-child)]:mb-2"
            data-testid="annotation-body-paragraph"
          >
            {children}
          </p>
        ),
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em>{children}</em>,
        code: ({ children }) => (
          <code className="rounded bg-muted/50 px-1 py-0.5 font-mono text-xs">{children}</code>
        ),
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-popover-foreground"
          >
            {children}
          </a>
        ),
      }}
    >
      {body}
    </Markdown>
  );
});
